// Офлайн-журнал кассы в IndexedDB. Событие сначала ложится в журнал,
// потом уходит на сервер: связь оборвалась в момент оплаты — чек цел.
// Сервер идемпотентен по eventId, поэтому «отправить ещё раз» безопасно.
import { get, set } from 'idb-keyval';
import type { SyncEvent } from '@dastarhan/contracts';
import { api } from '../api';

const KEY = 'dastarhan.queue.v1';
/** События, которые сервер не принял: деньги не должны пропадать молча */
const BAD_KEY = 'dastarhan.queue.rejected.v1';

async function read(): Promise<SyncEvent[]> {
  return (await get<SyncEvent[]>(KEY)) ?? [];
}

/**
 * Итог чека из события — сумма строк оплат.
 *
 * Поля `total` в событии нет: контракт заморожен, деньги сверяет
 * сервер при материализации. Экрану же итог нужен здесь и сейчас,
 * и честнее всего он складывается из того, что гость заплатил.
 * Чаевые сверх чека (tip) в итог не входят.
 */
const eventTotal = (e: SyncEvent): number => {
  const o = e as unknown as { payments?: { amount: number }[] };
  return (o.payments ?? []).reduce((s, p) => s + p.amount, 0);
};

export async function enqueue(event: SyncEvent): Promise<void> {
  const q = await read();
  q.push(event);
  await set(KEY, q);
}

export async function pendingCount(): Promise<number> {
  return (await read()).length;
}

/** Отправить накопленное. Возвращает, сколько осталось в очереди. */
/**
 * Отправить накопленное.
 *
 * Возвращает и остаток очереди, и признак связи — это РАЗНЫЕ вещи.
 * Раньше «онлайн» считали по пустой очереди: одно застрявшее событие
 * — и касса вечно показывала «Офлайн» при живой сети, а кассир искал
 * несуществующую поломку связи.
 */
export async function flushEx(token: string): Promise<{ left: number; reached: boolean }> {
  const q = await read();
  if (!q.length) return { left: 0, reached: true };
  try {
    const before = q.length;
    const left = await flush(token);
    return { left, reached: true };
  } catch {
    return { left: q.length, reached: false };
  }
}

export async function flush(token: string): Promise<number> {
  const q = await read();
  if (!q.length) return 0;
  try {
    const res = await api.sync(token, { events: q });
    const done = new Set([...res.accepted, ...res.duplicates]);
    /*
     * Отклонённые сервером НЕ ВЫБРАСЫВАЕМ молча.
     *
     * Раньше они снимались с очереди «чтобы не крутить вечно» —
     * и чек исчезал навсегда. Кассир видел сумму, гость заплатил,
     * а в отчёте пусто, и никто не знал почему.
     *
     * Теперь откладываем в отдельный ящик: очередь не забивается,
     * но деньги остаются на виду. Экран «Чеки» покажет их отдельно,
     * а владелец увидит, что разбирать.
     */
    const bad = await get<{ event: SyncEvent; reason: string; at: string }[]>(BAD_KEY) ?? [];
    for (const r of res.rejected) {
      done.add(r.eventId);
      const ev = q.find((e) => e.eventId === r.eventId);
      if (ev) bad.push({ event: ev, reason: r.reason, at: new Date().toISOString() });
    }
    if (bad.length > 0) await set(BAD_KEY, bad.slice(-200));
    const left = q.filter((e) => !done.has(e.eventId));
    await set(KEY, left);
    return left.length;
  } catch (e) {
    // Связи нет — очередь ждёт. Ошибку пробрасываем: наверху по ней
    // отличают «сервер не ответил» от «событие отклонено»
    throw e;
  }
}

/**
 * Чеки, которые ещё не ушли на сервер.
 *
 * Экран «Чеки смены» спрашивает сервер — и при офлайне показывает
 * пустоту, хотя кассир пробил двадцать чеков. Для него это выглядит
 * как потеря денег: он видел суммы, а списка нет.
 *
 * Здесь мы достаём их из той же очереди, что ждёт отправки. Они уже
 * настоящие: с номером, суммой и временем. Просто сервер о них
 * ещё не знает.
 */
export async function pendingChecks(): Promise<{
  orderId: string; number: number; total: number; at: string;
}[]> {
  const all = await read();
  return all
    .filter((e) => e.type === 'order.closed')
    .map((e) => {
      const o = e as unknown as { orderId: string; number: number; at: string };
      return { orderId: o.orderId, number: o.number, total: eventTotal(e), at: o.at };
    })
    .sort((a, b) => b.at.localeCompare(a.at));
}

/** Чеки, которые сервер не принял: причина и время попытки. */
export async function rejectedChecks(): Promise<{
  number: number; total: number; reason: string; at: string;
}[]> {
  const bad = await get<{ event: SyncEvent; reason: string; at: string }[]>(BAD_KEY) ?? [];
  return bad
    .filter((b) => b.event.type === 'order.closed')
    .map((b) => {
      const o = b.event as unknown as { number: number };
      return { number: o.number, total: eventTotal(b.event), reason: b.reason, at: b.at };
    })
    .reverse();
}
